import { useCallback, useMemo } from 'react';
import { useExcelExport } from './useExcelExport';
import {
    buildPriceChangeExportRequestBody,
    type ExportFilters,
} from '../services/exportRequests.service';

export interface ExportPriceChangeRequestsArgs {
    requestIds: string[] | null;
    filters?: ExportFilters;
    includeCostPricesAndMargins?: boolean;
}

interface UseExportRequestsResult {
    exporting: boolean;
    exportError: Error | undefined;
    exportRequests: (
        requestIds: string[] | null,
        filters?: ExportFilters,
        includeCostPricesAndMargins?: boolean,
    ) => Promise<void>;
}

export const useExportRequests = (): UseExportRequestsResult => {
    const config = useMemo(
        () => ({
            notificationId: 'price-change-requests',
            loadingTitle: 'Exporting Price Change Requests',
            loadingMessage:
                'Please wait while we export the price change requests',
            successTitle: 'Export Successful',
            successMessage: 'Your Excel file has been downloaded.',
            failedTitle: 'Export Failed' as const,
            filenamePrefix: 'price_change_requests',
            exportPath: '/export/price-change-requests',
            throwOnError: true,
            buildBody: ({
                requestIds,
                filters,
                includeCostPricesAndMargins = true,
            }: ExportPriceChangeRequestsArgs) =>
                buildPriceChangeExportRequestBody(requestIds, {
                    ...filters,
                    includeCostPricesAndMargins,
                }),
            validate: (args: ExportPriceChangeRequestsArgs) => {
                if (args.requestIds?.length === 0) {
                    return 'No requests selected. Please select at least one row to export.';
                }
                return null;
            },
        }),
        [],
    );

    const { exporting, exportError, runExport } =
        useExcelExport<ExportPriceChangeRequestsArgs>(config);

    const exportRequests = useCallback(
        async (
            requestIds: string[] | null,
            filters?: ExportFilters,
            includeCostPricesAndMargins = true,
        ) => {
            await runExport({
                requestIds,
                filters,
                includeCostPricesAndMargins,
            });
        },
        [runExport],
    );

    return { exporting, exportError, exportRequests };
};
